import type {
  ExtractPropTypes,
  ExtractPublicPropTypes,
  PropType,
  Ref,
} from "vue";
import type {
  DatePickerProps,
  MonthPickerProps,
  QuarterPickerProps,
  RangePickerProps,
  WeekPickerProps,
  YearPickerProps,
} from "../../../arco.types";
import type {
  ElCascaderProps,
  ElDatePickerProps,
  ElInputNumberProps,
  ElInputProps,
  ElSwitchProps,
  ElTimePickerProps,
} from "../../../element-plus.types";
import type { CheckboxPublicPropsType } from "../../checkbox/src/checkbox";
import type { DividerPublicPropsType } from "../../divider/src/divider";
import type { RadioPublicPropsType } from "../../radio/src/radio";
import type { SelectPublicPropsType } from "../../select/src/select";
import type Form from "./form.vue";

// el-* element-plus组件名称
type ELComp =
  | "el-input"
  | "el-input-number"
  | "el-date-picker"
  | "el-time-picker"
  | "el-cascader"
  | "el-switch";
const ElCompProps = {
  elInput: Object as PropType<ElInputProps>,
  elInputNumber: Object as PropType<ElInputNumberProps>,
  elDatePicker: Object as PropType<ElDatePickerProps>,
  elTimePicker: Object as PropType<ElTimePickerProps>,
  elCascader: Object as PropType<ElCascaderProps>,
  elSwitch: Object as PropType<ElSwitchProps>,
};

// i-* 自封装组件名称
type IComp =
  | "i-divider"
  | "i-select"
  | "i-radio"
  | "i-checkbox"
  | "i-date-picker";
const ICompProps = {
  iDivider: Object as PropType<DividerPublicPropsType>,
  iSelect: Object as PropType<SelectPublicPropsType>,
  iRadio: Object as PropType<RadioPublicPropsType>,
  iCheckbox: Object as PropType<CheckboxPublicPropsType>,
  iDatePicker: Object as PropType<ElDatePickerProps>,
};

// a-* arco design组件名称
type ArcoComp =
  | "a-range-picker"
  | "a-date-picker"
  | "a-week-picker"
  | "a-quarter-picker"
  | "a-year-picker"
  | "a-month-picker";
const ArcoCompProps = {
  aRangePicker: Object as PropType<RangePickerProps>,
  aDatePicker: Object as PropType<DatePickerProps>,
  aWeekPicker: Object as PropType<WeekPickerProps>,
  aQuarterPicker: Object as PropType<QuarterPickerProps>,
  aYearPicker: Object as PropType<YearPickerProps>,
  aMonthPicker: Object as PropType<MonthPickerProps>,
};

export type FormItemComp = ELComp | ArcoComp | IComp | "slot";

export const FormProps = {
  model: {
    type: Object,
    default: () => ({}),
  },
  rules: {
    type: Object,
    default: () => ({}),
  },
  inline: {
    type: Boolean,
    default: false,
  },
  labelPosition: {
    type: String as PropType<"left" | "right" | "top">,
    default: "right",
  },
  labelWidth: {
    type: [String, Number],
    default: "auto",
  },
  labelSuffix: {
    type: String,
    default: "",
  },
  hideRequiredAsterisk: {
    type: Boolean,
    default: false,
  },
  requireAsteriskPosition: {
    type: String as PropType<"left" | "right">,
    default: "left",
  },
  showMessage: {
    type: Boolean,
    default: true,
  },
  inlineMessage: {
    type: Boolean,
    default: false,
  },
  statusIcon: {
    type: Boolean,
    default: false,
  },
  validateOnRuleChange: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<"large" | "default" | "small" | "">,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  scrollToError: {
    type: Boolean,
    default: false,
  },
  scrollIntoViewOptions: {
    type: [Object, Boolean],
    default: false,
  },
  /**
   * 自定义的 props
   */
  // grid 模式下，每行显示的列数
  column: {
    type: Number,
    default: 1,
  },
  // 显示的表单字段
  fields: {
    type: Array as PropType<FormItemPropsType[]>,
    default: () => [],
  },
  action: Object,
};
export const FormItemProps = {
  prop: {
    type: String,
  },
  label: {
    type: String,
  },
  labelWidth: {
    type: [String, Number],
  },
  // 踩了一个坑，这里默认设置为true和false都会影响到表单验证(rules)
  // 体现为：必须手动调用formRef.validate(),否则不会触发表单验证。
  required: {
    type: Boolean,
    default: undefined,
  },
  rules: Object,
  error: String,
  showMessage: {
    type: Boolean,
    default: true,
  },
  inlineMessage: {
    type: [String, Boolean],
  },
  size: {
    type: String as PropType<"" | "large" | "default" | "small">,
  },
  validateStatus: {
    type: String as PropType<"" | "error" | "validating" | "success">,
  },
  /**
   * 自定义属性
   */
  modelValue: [String, Array, Boolean, Number, Object],
  // 列
  column: {
    type: Number,
    default: 1,
  },
  // 行
  row: {
    type: Number,
    default: 1,
  },
  className: {
    type: [String, Array],
    default: "",
  },
  style: {
    type: Object,
    default: () => ({}),
  },
  // 组件名称
  comp: {
    type: String as PropType<FormItemComp>,
    required: true,
  },
  action: Object,
  ...ElCompProps,
  ...ICompProps,
  ...ArcoCompProps,
};

export const formItemExcludeKeys = [
  "modelValue",
  "column",
  "row",
  "className",
  "comp",
  "elInput",
  "elInputNumber",
  "elDatePicker",
  "elTimePicker",
  "elCascader",
  "elSwitch",
  "iDivider",
  "iSelect",
  "iRadio",
  "iCheckbox",
  "pureUpload",
  "action",
];

export type ArrayAble<T> = T[] | T;

export type FormPropsType = ExtractPropTypes<typeof FormProps>;
export type FormItemPropsType = ExtractPublicPropTypes<typeof FormItemProps>;
export type FormInstance = InstanceType<typeof Form>;

/**
 * 设置 fields 的属性值
 */
export function iFormSetFieldValue(
  fields: Ref<FormItemPropsType[]>,
  prop: string,
  valOpt: Record<string, any>,
) {
  const index = fields.value.findIndex((item) => item.prop === prop);
  if (index === -1) {
    console.error(`field ${prop} is not found!`);
    return;
  }
  Object.keys(valOpt).forEach((key) => {
    if (key.includes(".")) {
      const keys = key.split(".");
      const [key1, key2, key3] = keys;
      if (keys.length === 2) {
        // @ts-ignore
        fields.value[index][key1][key2] = valOpt[key];
      } else if (keys.length === 3) {
        // @ts-ignore
        fields.value[index][key1][key2][key3] = valOpt[key];
      } else {
        console.error(`field ${prop} is error!`);
      }
    } else {
      // @ts-ignore
      fields.value[index][key] = valOpt[key];
    }
  });
}
